import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { create } from "zustand";
import { api } from "api";
import { useFetchStateHelper } from "hooks/useFetchState";
import { wait } from "utils/utils";

const convertGreeklishToGreek = (text) => {
  text = text.toLowerCase();
  text = text
    .replace(/th/g, "θ")
    .replace(/ch/g, "χ")
    .replace(/ps/g, "ψ")
    .replace(/ph/g, "φ");
  text = text
    .replace(/a/g, "α")
    .replace(/b/g, "β")
    .replace(/g/g, "γ")
    .replace(/d/g, "δ")
    .replace(/e/g, "ε")
    .replace(/z/g, "ζ")
    .replace(/h/g, "η")
    .replace(/i/g, "ι")
    .replace(/k/g, "κ")
    .replace(/l/g, "λ")
    .replace(/m/g, "μ")
    .replace(/n/g, "ν")
    .replace(/o/g, "ο")
    .replace(/p/g, "π")
    .replace(/r/g, "ρ")
    .replace(/s/g, "σ")
    .replace(/t/g, "τ")
    .replace(/u/g, "υ")
    .replace(/f/g, "φ")
    .replace(/x/g, "χ")
    .replace(/w/g, "ω");

  return text;
};

const normalizeGreek = (str) => {
  const greekStr = str
    .normalize("NFD") // Decompose accents
    .replace(/[\u0300-\u036f]/g, "") // Strip combining accents
    .replace(/[ς]/g, "σ") // Normalize final sigma to standard sigma
    .toLowerCase(); // Lowercase

  const normalized = convertGreeklishToGreek(greekStr);

  return normalized;
};

const filterOptionsCallback = (value, options) => {
  const allOptions = options || [];
  const normalizedInput = normalizeGreek(value);

  const filtered = allOptions.filter((option) => {
    const normalizedOption = normalizeGreek(option);
    return normalizedOption.startsWith(normalizedInput);
  });

  return filtered;
};

const useStore = create((set) => ({
  messages: [],
  history: [],
  options: [],
  filteredOptions: [],
  filterInput: "",
  lastUpdatedAt: "",
  isFilterInputDisabled: false,
  isFinal: false,
  shouldShowCropHelper: false,
  answerType: "",
  setMessages: (message) =>
    set((state) => ({ messages: [...state.messages, message] })),
  setLastUpdatedAt: (date) => set({ lastUpdatedAt: date }),
  addToHistory: ({ payload, response }) => {
    set((state) => ({ history: [...state.history, { payload, response }] }));
  },
  popFromHistory: () => {
    const state = useStore.getState();
    const newHistory = [...state.history].slice(0, -1);

    const latest = {
      ...newHistory[newHistory.length - 1],
    };

    set((state) => ({
      history: newHistory,
    }));

    return latest;
  },
  undoLastMessages: () => {
    set((state) => {
      const lastUserMessageIndex = [...state.messages]
        .reverse()
        .findIndex((message) => message.from === "user");

      if (lastUserMessageIndex === -1) return { messages: [] };

      const cutoffIndex = state.messages.length - lastUserMessageIndex - 1;
      return { messages: state.messages.slice(0, cutoffIndex) };
    });
  },
  setDataFromResponse: (data) => {
    const areCropOptionsTooMany =
      data.answerType === "crop" && data.choices?.length > 5;
    const shouldShowCropHelper = areCropOptionsTooMany;
    const shouldHideOptions = data.final || areCropOptionsTooMany;

    set({
      isFinal: data.final,
      answerType: data.answerType,
      options: data.choices,
      filteredOptions: shouldHideOptions ? [] : data.choices,
      shouldShowCropHelper,
      isFilterInputDisabled: data.choices?.length === 0,
      filterInput: "",
    });
  },
  clearFilterInput: () => set({ filterInput: "" }),
  disableFilterInput: () => set({ isFilterInputDisabled: true }),
  enableFilterInput: () => set({ isFilterInputDisabled: false }),
  filterOptions: (value) =>
    set((state) => {
      const shouldHideOptionsIfFilterInputIsSmall = state.answerType === "crop";
      const shouldShowCropHelper = shouldHideOptionsIfFilterInputIsSmall
        ? value.length < 3
        : false;
      return {
        filterInput: value,
        filteredOptions: shouldShowCropHelper
          ? []
          : filterOptionsCallback(value, state.options),
        shouldShowCropHelper,
      };
    }),
  resetState: () =>
    set({
      messages: [],
      history: [],
      options: [],
      filteredOptions: [],
      filterInput: "",
      isFilterInputDisabled: false,
      isFinal: false,
      shouldShowCropHelper: false,
      answerType: "",
    }),
}));

const useChatbotActions = () => {
  const answerType = useStore((state) => state.answerType);
  const history = useStore((state) => state.history);
  const setMessages = useStore((state) => state.setMessages);
  const setLastUpdatedAt = useStore((state) => state.setLastUpdatedAt);
  const setDataFromResponse = useStore((state) => state.setDataFromResponse);
  const addToHistory = useStore((state) => state.addToHistory);
  const undoLastMessages = useStore((state) => state.undoLastMessages);
  const filterAllOptions = useStore((state) => state.filterOptions);
  const disableFilterInput = useStore((state) => state.disableFilterInput);
  const resetState = useStore((state) => state.resetState);
  const popFromHistory = useStore((state) => state.popFromHistory);

  const answer = useCallback(
    async (selectedAnswer) => {
      let finalPayload = {};

      const isInitialFetch = !selectedAnswer;

      // If answerType exists, this is not the initial fetch
      if (!isInitialFetch) {
        const latestPayload = history[history.length - 1]?.payload;

        disableFilterInput();
        setMessages({ from: "user", text: selectedAnswer });
        finalPayload = {
          ...latestPayload,
          [answerType]: selectedAnswer,
        };
      }
      await wait(300);
      const data = await api.chatbot.sendChatbotAnswer(finalPayload);
      setMessages({ from: "bot", text: data.question });
      if (history.length === 0) {
        setLastUpdatedAt(data.lastUpdated);
      }
      addToHistory({ payload: finalPayload, response: data });
      setDataFromResponse(data);
    },
    [
      setMessages,
      setLastUpdatedAt,
      history,
      addToHistory,
      setDataFromResponse,
      disableFilterInput,
      answerType,
    ]
  );

  const { mutateAsync, ...queryResponse } = useMutation({
    queryKey: ["chatbot-answer"],
    mutationFn: (payload) => answer(payload),
  });

  const undoLast = useCallback(() => {
    const latestHistoryItem = popFromHistory();
    const { response } = latestHistoryItem;

    undoLastMessages();
    setDataFromResponse(response);
  }, [popFromHistory, setDataFromResponse, undoLastMessages]);

  const reset = useCallback(async () => {
    resetState();
    await mutateAsync();
  }, [mutateAsync, resetState]);

  const terminate = useCallback(() => {
    resetState();
  }, [resetState]);

  const filterOptions = useCallback(
    (value) => filterAllOptions(value),
    [filterAllOptions]
  );

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    answer: mutateAsync,
    fetchState,
    filterOptions,
    reset,
    terminate,
    undoLast,
  };
};

const useChatbotOptions = () => {
  const filteredOptions = useStore((state) => state.filteredOptions);
  const shouldShowCropHelper = useStore((state) => state.shouldShowCropHelper);
  const noMoreQuestions = useStore((state) => state.isFinal);

  return { filteredOptions, shouldShowCropHelper, noMoreQuestions };
};

const useChatbotFilter = () => {
  const isFilterInputDisabled = useStore(
    (state) => state.isFilterInputDisabled
  );
  const filterInput = useStore((state) => state.filterInput);
  const isUndoEnabled = useStore((state) => state.history.length > 1);

  return { filterInput, isFilterInputDisabled, isUndoEnabled };
};

const useChatbotMessages = () => {
  const messages = useStore((state) => state.messages);
  const isFinal = useStore((state) => state.isFinal);

  return { messages, isFinal };
};

const useChatbotLastUpdatedAt = () => {
  const lastUpdatedAt = useStore((state) => state.lastUpdatedAt);
  return { lastUpdatedAt };
};

export {
  useChatbotActions,
  useChatbotOptions,
  useChatbotLastUpdatedAt,
  useChatbotMessages,
  useChatbotFilter,
};
